import { OverlayRef } from '@angular/cdk/overlay';
import { DomPortal, DomPortalOutlet } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  input,
  signal,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
  selector: 'abb-side-panel',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="bg-abb-background flex flex-col shadow-lg transition-all h-[100vh] min-w-96"
      [ngClass]="{
        'opacity-0 translate-x-full': !open(),
        'translate-x-0 opacity-100': open()
      }"
    >
      <div class="flex justify-between items-center gap-x-4 pt-4 px-8">
        @if (title()){
        <h2 class="text-abb-font font-semibold text-3xl py-3">{{ title() }}</h2>
        } @else {
        <ng-content select="[header]"></ng-content>
        }
      </div>
      <div
        class="flex overflow-auto flex-1 flex-col min-h-0 px-8"
        [ngClass]="contentWrapperClass()"
      >
        <ng-content></ng-content>
      </div>
      <ng-content select="[footer]"></ng-content>
    </div>
    @if(hasBackdrop()) {
    <div
      #backdrop
      class="fixed z-side-panel-backdrop inset-0 bg-abb-black/40 transition-opacity"
      (click)="onBackdropClick()"
      [ngStyle]="{ opacity: backdropOpacity() }"
    ></div>
    }
  `,
  styles: ``,
})
export class AbbSidePanelComponent implements AfterViewInit, OnDestroy {
  title = input('');
  contentWrapperClass = input('');
  closable = input(true);
  backdropClickClosable = input(false);
  hasBackdrop = input(true);
  onBeforeClose = input<(() => Promise<boolean>) | undefined>();
  onClose = input<() => void>();
  open = signal(false);
  subscriptions: Subscription[] = [];
  backdropOpacity = signal(0);
  private portal: DomPortal<HTMLDivElement> | undefined;
  @ViewChild('backdrop') backdrop: ElementRef<HTMLDivElement> | undefined;
  constructor(protected overlayRef: OverlayRef, router: Router) {
    // close overlay if route changes (e.g. user clicked on another menu entry)
    this.subscriptions.push(
      router.events.subscribe((e) => {
        if (e instanceof NavigationStart) {
          this.overlayRef.detach();
        }
      })
    );
  }
  onBackdropClick() {
    if (this.backdropClickClosable() && this.closable()) {
      this.handleClose();
    }
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.portal?.detach();
  }
  ngAfterViewInit(): void {
    requestAnimationFrame(() => this.open.set(true));
    if (this.backdrop && this.hasBackdrop()) {
      this.portal = new DomPortal(this.backdrop.nativeElement);
      this.portal.attach(
        new DomPortalOutlet(
          document.body,
          undefined,
          undefined,
          undefined,
          document
        )
      );
      requestAnimationFrame(() => {
        this.backdropOpacity.set(1);
      });
    }
  }
  async handleClose() {
    const onBeforeClose = this.onBeforeClose();
    if (onBeforeClose) {
      const canContinue = await onBeforeClose();
      if (!canContinue) {
        return;
      }
    }
    this.onClose()?.();
    this.dispose();
  }

  dispose() {
    this.open.set(false);
    setTimeout(() => {
      this.overlayRef.dispose();
    }, 150);
  }
}
